import {
  CheckCircleOutlined,
  DeleteOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { Alert, Button, Card, Collapse, Space, Table, Tag, Typography } from 'antd'
import type { JSX } from 'react'
import styled from 'styled-components'

import type { CloudfrontStatus } from './CloudfrontStatusTag'
import { CLOUDFRONT_STATUS_LABEL, CloudfrontStatusTag } from './CloudfrontStatusTag'

type DnsRecord = {
  type: string
  host: string
  value: string
  ttl: number
}

type DomainStatusViewProps = {
  canWrite: boolean
  cloudfrontStatus: CloudfrontStatus
  dnsRecords: DnsRecord[]
  domain: string
  isRemoving: boolean
  isVerifying: boolean
  onOpenRemove: () => void
  onVerify: () => void
}

const StyledDomainCard = styled(Card)`
  margin-bottom: 16px;

  .ant-card-body {
    padding: 16px 20px;
  }
`

const StyledDomainRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const StyledDomainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

const StyledDomainActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const StyledSectionHeading = styled(Typography.Text)`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  margin: 32px 0 12px;
`

const TYPE_TAG_COLOR: Record<string, string> = {
  CNAME: 'blue',
  TXT: 'purple',
  A: 'green',
}

const DNS_COLUMNS = [
  {
    title: 'TYPE',
    dataIndex: 'type',
    key: 'type',
    width: 80,
    render: (type: string) => <Tag color={TYPE_TAG_COLOR[type] ?? 'default'}>{type}</Tag>,
  },
  {
    title: 'HOST',
    dataIndex: 'host',
    key: 'host',
    render: (host: string) => <Typography.Text copyable>{host}</Typography.Text>,
  },
  {
    title: 'VALUE',
    dataIndex: 'value',
    key: 'value',
    render: (value: string) => <Typography.Text copyable>{value}</Typography.Text>,
  },
  {
    title: 'TTL',
    dataIndex: 'ttl',
    key: 'ttl',
    width: 80,
    render: (ttl: number) => `${ttl}s`,
  },
]

const DNS_GUIDE_ITEMS = [
  {
    key: 'guide',
    label: (
      <Space size={6}>
        <InfoCircleOutlined />
        <Typography.Text strong style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          DNS Configuration Guide
        </Typography.Text>
      </Space>
    ),
    children: (
      <Typography.Paragraph style={{ margin: 0 }}>
        Log into your DNS provider (Cloudflare, Route 53, GoDaddy, etc.).
        <br />
        Publish the CNAME record shown above.
        <br />
        Propagation typically takes 5–30 minutes but can take up to 48 hours.
        <br />
        Click <Typography.Text strong>Verify Now</Typography.Text> once the record is live. SSL provisions automatically
        on success.
      </Typography.Paragraph>
    ),
  },
]

export function DomainStatusView({
  canWrite,
  cloudfrontStatus,
  dnsRecords,
  domain,
  isRemoving,
  isVerifying,
  onOpenRemove,
  onVerify,
}: DomainStatusViewProps): JSX.Element {
  const isActive = cloudfrontStatus === 'active'
  const isFailed = cloudfrontStatus === 'failed'

  return (
    <>
      <StyledDomainCard>
        <StyledDomainRow>
          <StyledDomainInfo data-testid="domain-info">
            <GlobalOutlined style={{ fontSize: 20, color: '#1677ff', flexShrink: 0 }} />
            <div>
              <Typography.Text strong style={{ fontSize: 15, display: 'block' }}>
                {domain}
              </Typography.Text>
              <Typography.Text style={{ fontSize: 12 }} type="secondary">
                {CLOUDFRONT_STATUS_LABEL[cloudfrontStatus]}
              </Typography.Text>
            </div>
          </StyledDomainInfo>

          <StyledDomainActions>
            <CloudfrontStatusTag status={cloudfrontStatus} />

            {!isActive && (
              <Button
                disabled={!canWrite}
                icon={<SyncOutlined />}
                loading={isVerifying}
                onClick={onVerify}
                type="primary"
              >
                Verify Now
              </Button>
            )}

            <Button
              aria-label="Remove Domain"
              danger
              disabled={!canWrite}
              icon={<DeleteOutlined />}
              loading={isRemoving}
              onClick={onOpenRemove}
              type="text"
            />
          </StyledDomainActions>
        </StyledDomainRow>
      </StyledDomainCard>

      {isActive ? (
        <Alert
          icon={<CheckCircleOutlined />}
          message={
            <>
              <Typography.Text strong>Domain verified.</Typography.Text> Your custom domain is active and SSL is
              provisioned.
            </>
          }
          showIcon
          type="success"
        />
      ) : isFailed ? (
        <Alert
          message={
            <>
              <Typography.Text strong>Provisioning failed.</Typography.Text> Check the DNS record below is correctly
              published and click Verify Now, or remove the domain and re-add it.
            </>
          }
          showIcon
          type="error"
        />
      ) : (
        <Alert
          message={
            <>
              <Typography.Text strong>Awaiting DNS verification.</Typography.Text> Add the records below at your DNS
              provider, then click <Typography.Text strong>Verify Now</Typography.Text>.
            </>
          }
          showIcon
          type="warning"
        />
      )}

      {dnsRecords.length > 0 && (
        <>
          <StyledSectionHeading>DNS Records</StyledSectionHeading>
          <Table columns={DNS_COLUMNS} dataSource={dnsRecords} pagination={false} rowKey="host" size="small" />
        </>
      )}

      <Collapse
        ghost
        items={DNS_GUIDE_ITEMS}
        style={{ marginTop: '12px', border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}
      />
    </>
  )
}
